import React from 'react';
import { cn } from '@/utils/helpers';

// 容器尺寸类型
export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

// 容器属性接口
export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: ContainerSize;
  centered?: boolean;
  padding?: boolean;
  children: React.ReactNode;
}

// 页面容器属性接口
export interface PageContainerProps extends ContainerProps {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  sidebar?: React.ReactNode;
  loading?: boolean;
  error?: React.ReactNode;
}

// 尺寸样式映射
const sizeStyles: Record<ContainerSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md lg:max-w-2xl',
  lg: 'max-w-lg lg:max-w-4xl',
  xl: 'max-w-xl lg:max-w-6xl',
  full: 'max-w-full'
};

// 基础容器组件
export const Container: React.FC<ContainerProps> = ({
  size = 'md',
  centered = true,
  padding = true,
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        // 基础样式
        'w-full',
        // 尺寸样式
        sizeStyles[size],
        // 居中样式
        centered && 'mx-auto',
        // 内边距样式 - 移动端优化
        padding && 'px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8',
        // 自定义类名
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// 页面容器组件
export const PageContainer: React.FC<PageContainerProps> = ({
  header,
  footer,
  sidebar,
  loading = false,
  error,
  children,
  className,
  ...containerProps
}) => {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-primary">
        <div className="text-center glass-card p-8 rounded-3xl animate-fade-scale">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6"></div>
          <p className="text-white text-lg font-semibold">✨ 加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-primary px-4">
        <div className="text-center max-w-md mx-auto">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen', className)}>
      {/* 头部 */}
      {header}

      {/* 主要内容区域 */}
      <main className={cn('flex-1 safe-area-inset', sidebar && 'lg:flex')}>
        {/* 侧边栏 */}
        {sidebar && (
          <aside className="lg:w-64 lg:flex-shrink-0 mobile:hidden">
            <div className="glass-card m-4 p-4 rounded-2xl">
              {sidebar}
            </div>
          </aside>
        )}

        {/* 内容容器 */}
        <div className="flex-1 min-w-0">
          <Container {...containerProps}>
            {children}
          </Container>
        </div>
      </main>

      {/* 底部 */}
      {footer}
    </div>
  );
};

// 卡片容器组件
export interface CardContainerProps extends ContainerProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  variant?: 'default' | 'outlined' | 'elevated';
}

export const CardContainer: React.FC<CardContainerProps> = ({
  title,
  subtitle,
  actions,
  variant = 'default',
  children,
  className,
  ...containerProps
}) => {
  const variantStyles = {
    default: 'bg-white border border-gray-200 shadow-sm',
    outlined: 'bg-white border-2 border-gray-300',
    elevated: 'bg-white shadow-lg border-0'
  };

  return (
    <Container
      {...containerProps}
      className={cn(
        'rounded-lg',
        variantStyles[variant],
        className
      )}
    >
      {/* 头部 */}
      {(title || subtitle || actions) && (
        <div className="flex items-start justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="min-w-0 flex-1">
            {title && (
              <h2 className="text-xl font-semibold text-gray-900 truncate">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-sm text-gray-600 mt-1">
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex-shrink-0 ml-4">
              {actions}
            </div>
          )}
        </div>
      )}

      {/* 内容 */}
      {children}
    </Container>
  );
};

// 网格容器组件
export interface GridContainerProps extends ContainerProps {
  cols?: 1 | 2 | 3 | 4 | 6 | 12;
  gap?: 'sm' | 'md' | 'lg';
  responsive?: boolean;
}

export const GridContainer: React.FC<GridContainerProps> = ({
  cols = 1,
  gap = 'md',
  responsive = true,
  children,
  className,
  ...containerProps
}) => {
  const colsStyles = {
    1: 'grid-cols-1',
    2: responsive ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2',
    3: responsive ? 'grid-cols-1 xs:grid-cols-2 lg:grid-cols-3' : 'grid-cols-3',
    4: responsive ? 'grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-4',
    6: responsive ? 'grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 lg:grid-cols-6' : 'grid-cols-6',
    12: responsive ? 'grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12' : 'grid-cols-12'
  };

  const gapStyles = {
    sm: 'gap-2 sm:gap-3',
    md: 'gap-3 sm:gap-4 lg:gap-6',
    lg: 'gap-4 sm:gap-6 lg:gap-8'
  };

  return (
    <Container
      {...containerProps}
      className={cn(
        'grid',
        colsStyles[cols],
        gapStyles[gap],
        className
      )}
    >
      {children}
    </Container>
  );
};

// 分栏容器组件
export interface SectionContainerProps extends ContainerProps {
  spacing?: 'sm' | 'md' | 'lg' | 'xl';
}

export const SectionContainer: React.FC<SectionContainerProps> = ({
  spacing = 'lg',
  children,
  className,
  ...containerProps
}) => {
  const spacingStyles = {
    sm: 'space-y-3 sm:space-y-4',
    md: 'space-y-4 sm:space-y-6',
    lg: 'space-y-6 sm:space-y-8 lg:space-y-12',
    xl: 'space-y-8 sm:space-y-12 lg:space-y-16'
  };

  return (
    <Container
      {...containerProps}
      className={cn(
        spacingStyles[spacing],
        className
      )}
    >
      {children}
    </Container>
  );
};

// 响应式容器组件
export interface ResponsiveContainerProps extends ContainerProps {
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl';
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  breakpoint = 'lg',
  children,
  className,
  ...containerProps
}) => {
  const breakpointStyles = {
    sm: 'max-w-full xs:max-w-sm sm:max-w-md',
    md: 'max-w-full xs:max-w-sm sm:max-w-md md:max-w-lg',
    lg: 'max-w-full xs:max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl',
    xl: 'max-w-full xs:max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl'
  };

  return (
    <Container
      {...containerProps}
      size="full"
      className={cn(
        breakpointStyles[breakpoint],
        className
      )}
    >
      {children}
    </Container>
  );
};

export default Container;